// Login.jsx
import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import logo from "../assets/Product_logo.png";
import { API_URL } from "../config";
import { showPopup } from "../utils/popupService";
import OtpVerificationModal from "../components/OtpVerificationModal";

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpLoading] = useState(false);
  const altchaRef = useRef(null);
  const [altchaPayload, setAltchaPayload] = useState(null);

  useEffect(() => {
    const handleStateChange = (ev) => {
      if (ev.detail.state === "verified") {
        setAltchaPayload(ev.detail.payload);
      } else {
        setAltchaPayload(null);
      }
    };

    const widget = altchaRef.current;
    if (widget) {
      widget.addEventListener("statechange", handleStateChange);
      return () => widget.removeEventListener("statechange", handleStateChange);
    }
  }, []);

  const handleLogin = async () => {
    if (!email || !password) {
      showPopup({
        type: "error",
        title: "Error",
        message: "Please enter username and password",
        buttons: ["OK"]
      });
      return;
    }

    try {
      setLoading(true);

      // 1. Verify Captcha first
      const altchaData = altchaPayload;
      if (!altchaData) {
        showPopup({
          type: "error",
          title: "Captcha Required",
          message: "Please solve the captcha to continue",
          buttons: ["OK"]
        });
        setLoading(false);
        return;
      }

      const captchaVerifyData = new URLSearchParams();
      captchaVerifyData.append("altcha", altchaData);
      captchaVerifyData.append("name", email);

      const verifyResponse = await fetch(`${API_URL}/captcha/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: captchaVerifyData.toString(),
      });

      if (!verifyResponse.ok) {
        showPopup({
          type: "error",
          title: "Verification Failed",
          message: "Captcha verification failed. Please try again.",
          buttons: ["OK"]
        });
        setLoading(false);
        return;
      }

      // 2. Proceed with actual Login
      const formData = new URLSearchParams();
      formData.append("username", email);
      formData.append("password", password);

      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      });

      const data = await response.json();

      if (!response.ok) {
        showPopup({
          type: "error",
          title: "Login Failed",
          message: data.detail || "Invalid credentials",
          buttons: ["OK"]
        });
        setLoading(false);
        return;
      }

      localStorage.setItem("access_token", data.access_token);
      if (data.refresh_token) {
        localStorage.setItem("refresh_token", data.refresh_token);
      }
      localStorage.setItem("token_type", data.token_type || "bearer");

      // Fetch profile to get is_admin status
      try {
        const profileResponse = await fetch(`${API_URL}/auth/profile`, {
          headers: {
            "Authorization": `Bearer ${data.access_token}`,
            "Accept": "application/json"
          }
        });

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          localStorage.setItem("is_admin", profileData.is_admin === true ? "true" : "false");
          localStorage.setItem("user_data", JSON.stringify(profileData));

          if (profileData.is_admin === true) {
            navigate("/admin");
          } else {
            navigate("/home");
          }
        } else {
          // Fallback if profile fetch fails
          localStorage.setItem("is_admin", "false");
          navigate("/home");
        }
      } catch (profileError) {
        console.error("Profile fetch error:", profileError);
        localStorage.setItem("is_admin", "false");
        navigate("/home");
      }
    } catch (error) {
      console.error("Login fetch error:", error);
      showPopup({
        type: "error",
        title: "Network Error",
        message: "An error occurred during login. Please try again.",
        buttons: ["OK"]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = () => {
    setShowOtpModal(true);
  };

  const handleOtpVerified = async (data) => {
    // data contains access_token, etc. from /auth/login-otp/verify
    localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) {
      localStorage.setItem("refresh_token", data.refresh_token);
    }
    localStorage.setItem("token_type", data.token_type || "bearer");

    // Fetch profile and navigate
    await fetchProfile(data.access_token);
  };

  const fetchProfile = async (token) => {
    try {
      const profileResponse = await fetch(`${API_URL}/auth/profile`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        localStorage.setItem("is_admin", profileData.is_admin === true ? "true" : "false");
        localStorage.setItem("user_data", JSON.stringify(profileData));

        if (profileData.is_admin === true) {
          navigate("/admin");
        } else {
          navigate("/home");
        }
      } else {
        localStorage.setItem("is_admin", "false");
        navigate("/home");
      }
    } catch (profileError) {
      console.error("Profile fetch error:", profileError);
      localStorage.setItem("is_admin", "false");
      navigate("/home");
    }
  };

  return (
    <div style={styles.wrapper}>
      <OtpVerificationModal
        isOpen={showOtpModal}
        email={null}
        title="Login OTP"
        requestOtpUrl={`${API_URL}/auth/login-otp/request`}
        verifyUrl={`${API_URL}/auth/login-otp/verify`}
        resendUrl={`${API_URL}/auth/resend-otp`}
        resendPurpose="login"
        onVerified={handleOtpVerified}
        onCancel={() => setShowOtpModal(false)}
      />
      <div style={styles.card}>
        <img src={logo} alt="Logo" style={styles.logo} />
        <h2 style={styles.heading}>Welcome Back</h2>
        <p style={styles.subtitle}>Login to manage your legal cases</p>

        <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
          {/* Username */}
          <div style={styles.inputContainer}>
            <input
              type="text"
              placeholder="Username / Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
            />
          </div>

          {/* ✅ PERFECTLY ALIGNED PASSWORD FIELD */}
          <div style={styles.passwordContainer}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.passwordInput}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={styles.eyeBtn}
              aria-label="Toggle password visibility"
            >
              {showPassword ? "🔓" : "🔒"}

            </button>
          </div>

          {/* Altcha Widget styled like requested image */}
          <div style={{ marginBottom: 16, position: "relative" }}>
            <altcha-widget
              ref={altchaRef}
              challengeurl={`${API_URL}/captcha/challenge`}
              hidefooter
              hidelogo
              strings={JSON.stringify({ ready: "I'm not a robot", verifying: "Verifying...", verified: "Verified" })}
              style={{
                "--altcha-max-width": "100%",
                "--altcha-border-radius": "8px",
                "--altcha-color-border": altchaPayload ? "#16a34a" : "#d1d5db",
                "--altcha-color-primary": altchaPayload ? "#16a34a" : "#2563eb",
                "--altcha-color": altchaPayload ? "#16a34a" : "#2563eb",
                "--altcha-primary-color": altchaPayload ? "#16a34a" : "#2563eb",
                "--altcha-color-text": altchaPayload ? "#16a34a" : "#4b5563",
                "--altcha-color-base": "#ffffff",
                accentColor: altchaPayload ? "#16a34a" : "#2563eb",
                fontWeight: 500,
                fontSize: "14px",
                transition: "all 0.3s ease"
              }}
            ></altcha-widget>
            {/* Shield Icon overlay */}
            <div
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: altchaPayload ? "#16a34a" : "#2563eb",
                transition: "color 0.3s ease"
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
          </div>

          <button
            type="submit"
            style={styles.loginBtn}
            disabled={loading || otpLoading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>

          <div style={styles.divider}>OR</div>

          <button
            type="button"
            onClick={handleRequestOtp}
            style={styles.otpLinkBtn}
            disabled={loading || otpLoading}
          >
            {otpLoading ? "Sending OTP..." : "Login with OTP"}
          </button>
        </form>

        <p style={styles.forgotWrapper}>
          <Link to="/forgot-password" style={styles.forgotLink}>
            Forgot Password?
          </Link>
        </p>

        <p style={styles.signup}>
          Don’t have an account?{" "}
          <Link to="/signup" style={styles.signupLink}>
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const styles = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 40%, #eef2ff 100%)",
  },
  card: {
    width: 380,
    background: "#fff",
    padding: "32px 28px",
    borderRadius: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    textAlign: "center",
  },
  logo: {
    width: 100,
    height: 120,
    marginBottom: 1,
  },
  heading: {
    fontSize: 22,
    fontWeight: 600,
    marginBottom: 4,
    marginTop: 12,
  },
  subtitle: {
    fontSize: 14,
    color: "#6b7280",
    marginBottom: 22,
  },

  inputContainer: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    height: 44,
    marginBottom: 16,
    // paddingRight: 8
  },
  input: {
    flex: 1,
    height: "100%",
    border: "none",
    padding: "0 12px",
    fontSize: 14,
    outline: "none",
    borderRadius: 8,
  },

  /* 🔥 FIXED ALIGNMENT HERE */
  passwordContainer: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    height: 44,
    marginBottom: 16,
    paddingRight: 8,
  },
  passwordInput: {
    flex: 1,
    height: "100%",
    border: "none",
    padding: "0 12px",
    fontSize: 14,
    outline: "none",
    borderRadius: 8,
  },
  eyeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },

  loginBtn: {
    width: "100%",
    height: 44,
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
  },
  divider: {
    margin: "12px 0",
    fontSize: "14px",
    color: "#6b7280",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },
  otpLinkBtn: {
    width: "100%",
    height: 44,
    background: "transparent",
    color: "#2563eb",
    border: "1px solid #2563eb",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  googleBtn: {
    width: "100%",
    height: 42,
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#fff",
    marginBottom: 12,
  },
  signup: {
    marginTop: 8,
    fontSize: 13,
  },
  signupLink: {
    color: "#2563eb",
    textDecoration: "none",
    fontWeight: 500,
  },
  forgotWrapper: {
    marginTop: 12,
    marginBottom: 4,
    fontSize: 13,
    textAlign: "center",
  },
  forgotLink: {
    color: "#6b7280",
    textDecoration: "none",
    fontSize: 13,
  },
};
